const isArray = require('lodash/isArray')
const isEmpty = require('lodash/isEmpty')

/**
 * Simplified packout algorithm for facility ordering.
 *
 * Given a requested quantity (in generic units) and a list of available
 * pack sizes within the same generic group, suggests the best combination
 * of packs to fulfill the order.
 *
 * Strategy: greedy, largest-pack-first. Uses the biggest packs that fit,
 * then fills the remainder with smaller packs. If the smallest available
 * pack is still larger than the remainder, includes one unit of it
 * (slight overpack is preferable to underdelivery).
 *
 * @param {Object} params
 * @param {number} params.requestedQuantity - Quantity in generic units
 * @param {Array} params.products - Pack sizes available at the warehouse,
 *   each with { productId, genericFactor, ledger }
 * @returns {Object} Map of { [productId]: quantityInGenericUnits }
 */
function suggestPackout ({ requestedQuantity, products } = {}) {
  if (
    !isFinite(requestedQuantity) ||
    requestedQuantity <= 0 ||
    !isArray(products) ||
    isEmpty(products)
  ) {
    return {}
  }

  // Only consider products with stock and a valid genericFactor.
  const available = products
    .filter(p => p.genericFactor > 0 && (p.ledger || 0) > 0)
    .map(p => ({
      productId: p.productId,
      genericFactor: p.genericFactor,
      stock: p.ledger
    }))
    .sort((a, b) => b.genericFactor - a.genericFactor)

  if (!available.length) {
    return {}
  }

  const result = {}
  let remaining = requestedQuantity

  // Pass 1: greedily fill using largest packs first.
  for (const pack of available) {
    if (remaining <= 0) break

    const packsNeeded = Math.floor(remaining / pack.genericFactor)
    const maxPacks = Math.floor(pack.stock / pack.genericFactor)
    const packsToUse = Math.min(packsNeeded, maxPacks)

    if (packsToUse > 0) {
      const quantity = packsToUse * pack.genericFactor
      result[pack.productId] = quantity
      remaining -= quantity
      pack.stock -= quantity
    }
  }

  // Pass 2: if there is a remainder, top up with the smallest pack that has stock.
  if (remaining > 0) {
    const smallestWithStock = available.slice().reverse().find(p => p.stock >= p.genericFactor)
    if (smallestWithStock) {
      const { productId, genericFactor } = smallestWithStock
      result[productId] = (result[productId] || 0) + genericFactor
      remaining -= genericFactor
    }
  }

  return result
}

exports.suggestPackout = suggestPackout
